import { useState } from "react";
import styled from "styled-components";

import { PrimaryButton } from "../../components/Buttons";
import { ThemeProps } from "../../style/themes/types";
import { Product, ProductFeature, ProductId } from "./types";

const ProductCard = styled.li`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: center;
  /* width: 54.86rem; */

  background: #fff;
  border-radius: 0.36rem;
  box-shadow: 0px 0px 30px rgba(0, 0, 0, 0.08);
  margin-bottom: 1.43rem;
  margin-top: 1.43rem;
`;

const SelectedProductCard = styled(ProductCard)`
  border: 3px solid ${props => props.theme.titles};
`;

const ProductCardLeftSection = styled.div`
  order: 0;
  flex-grow: 0;
  flex-shrink: 0;

  display: flex;
  justify-content: center;

  width: 8.86rem;
`;

const ProductCardMiddleSection = styled.div`
  order: 1;
  flex-grow: 1;

  border-left: 0.5px solid #7676769b;
  border-right: 0.5px solid #7676769b;
  padding-left: 1.79rem;
  padding-top: 1.29rem;
  padding-right: 1.36rem;
  padding-bottom: 1.79rem;
`;

const ProductCardRightSection = styled.div`
  order: 2;
  flex-grow: 0;
  flex-shrink: 0;

  display: flex;
  justify-content: center;

  width: 13.5rem;
`;

const ProductIcon = styled.div``;

const ProductTitle = styled.div<ThemeProps>`
  color: ${props => props.theme.texts};
  font-size: 1.79rem;
  font-weight: 700;
  line-height: 2.4rem;
  margin-bottom: 1.29rem;
`;

const ProductDescription = styled.div<ThemeProps>`
  color: ${props => props.theme.texts};
  font-size: 1rem;
  line-height: 1.5rem;
`;

const ProductFeaturesContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: space-between;
  align-items: center;
  margin-top: 1.93rem;
  column-gap: 0.43rem;
`;

const ProductFeatureCard = styled.div<ThemeProps>`
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;

  background-color: ${props => props.theme.featureBackground};
  border-radius: 0.36rem;
  height: 5.64rem;
  padding-bottom: 0.86rem;
  padding-left: 0.57rem;
  padding-right: 0.57rem;
  padding-top: 0.86rem;
`;

const ProductFeatureText = styled.div`
  font-weight: 500;
  line-height: 150%;
  font-size: 0.86rem;
`;

const ProductFeatureBoldText = styled.div`
  font-family: "WorkSans";
  font-style: normal;
  font-weight: bold;
  font-size: 1rem;
  line-height: 1.29rem;

  display: flex;
  align-items: center;
  text-align: center;

  flex-grow: 0;
  max-width: 6rem;
`;
interface ProductFeatureProps {
  feature: ProductFeature;
}
function ProductFeatureComponent({ feature }: ProductFeatureProps) {
  const { text, boldText } = feature;

  if (!text && !boldText) {
    return null;
  }

  return (
    <ProductFeatureCard>
      {feature.text && <ProductFeatureText>{feature.text}</ProductFeatureText>}
      {feature.boldText && (
        <ProductFeatureBoldText>{feature.boldText}</ProductFeatureBoldText>
      )}
    </ProductFeatureCard>
  );
}

interface ProductProps {
  product: Product;
  selected: boolean;
  updateOperator: (id: ProductId) => Promise<boolean>;
}
function ProductComponent({ product, selected, updateOperator }: ProductProps) {
  const DynamicCard = selected ? SelectedProductCard : ProductCard;

  const [buttonText, setButtonText] = useState<string>("Activar");
  const [updatingOperator, setUpdatingOperator] = useState<boolean>(false);

  const handleButtonClick = (id: ProductId) => {
    setUpdatingOperator(true);
    updateOperator(id)
      .then(success => {
        if (success) {
          setButtonText("Activado");
        } else {
          setButtonText("No activado");
        }
        setUpdatingOperator(false);
      })
      .catch(_ => {
        setButtonText("No activado");
        setUpdatingOperator(false);
      });
  };
  return (
    <DynamicCard>
      <ProductCardLeftSection>
        <ProductIcon>(icon)</ProductIcon>
      </ProductCardLeftSection>

      <ProductCardMiddleSection>
        <ProductTitle>{product.title}</ProductTitle>
        <ProductDescription>{product.description}</ProductDescription>
        <ProductFeaturesContainer>
          {product.features.map((feature, i) => (
            <ProductFeatureComponent
              key={`product-${product.id}-feature-${i}`}
              feature={feature}
            />
          ))}
        </ProductFeaturesContainer>
      </ProductCardMiddleSection>

      <ProductCardRightSection>
        <PrimaryButton
          loading={updatingOperator}
          onClick={() => handleButtonClick(product.id)}>
          {buttonText}
        </PrimaryButton>
      </ProductCardRightSection>
    </DynamicCard>
  );
}

const StyledProductList = styled.ol`
  display: flex;
  flex-direction: column;
  flex-wrap: nowrap;
  justify-content: flex-start;
  align-items: flex-start;
  width: 800px;
`;

interface ProductListProps {
  products: Product[];
  updateOperator: (productId: ProductId) => Promise<boolean>;
}

export default function ProductList({ products, updateOperator }: ProductListProps) {
  // TODO: set the `selected` value depending on whether the product is in the basket or not
  return (
    <StyledProductList>
      {products.map(product => (
        <ProductComponent
          key={`product-${product.id}`}
          product={product}
          selected={false}
          updateOperator={async (id: ProductId) => updateOperator(id)}
        />
      ))}
    </StyledProductList>
  );
}
